import { css } from '@emotion/react'
import { useCallback, useEffect, useState } from 'react'
import { inject, observer } from 'mobx-react'
import { Link } from 'react-router'
import _ from 'lodash'

import { numberToDollars, primeKeyboard, withRouterProps } from '../../../utils'
import { Container, Loading } from '../../../components'
import Button from '../../../components/Button'
import InvestmentsDisplay from './components/InvestmentsDisplay'
import InstitutionalInvestmentsDisplay from './components/InstitutionalInvestmentsDisplay'
import useInvestmentsRefresh from './useInvestmentsRefresh'
import { UPLOAD_COPY, UploadStatementButton, useStatementUpload } from './upload'
import {
  AddInvestmentPill,
  BalanceDigits,
  BalanceLabel,
  BalanceSummary,
  BalanceSymbol,
  BalanceValue,
  DoneRow,
  EmptyStateBox,
  HeaderMain,
  InvestmentActions,
  PageHeader,
  PageHeading,
  ReviewNote,
  SkipButton,
  SkipTextLink,
} from './AddInvestmentsStyle'

const ErrorMessage = () => (
  <div
    css={css`
      color: crimson;
    `}>
    Something went wrong, please try again later.
  </div>
)

const AddInvestments = props => {
  const [status, setStatus] = useState('loading')
  const { account, getInvestments, getAccount } = props.account
  const { type, id, accountType } = props.match.params
  const { getFeatures, institutionalAccountUnderReview, institutionalAccount } = props.store
  const isInstitutionalAccount = props.account?.account?.id === institutionalAccount?.id
  const { openUpload, isMobile } = useStatementUpload({
    store: props.store,
    type,
    accountType,
    accountId: id,
  })

  useInvestmentsRefresh({ store: props.store, account: props.account, type, accountId: id })

  const fetchInitialState = useCallback(async () => {
    try {
      if (!account) {
        await getAccount({ type, id })
      }

      await getInvestments({ type, id })

      setStatus('done')
    } catch (err) {
      console.error(err)
      setStatus('error')
    }
  }, [account, getAccount, getInvestments, id, type])

  useEffect(() => {
    fetchInitialState()

    const fetchFeatures = async () => {
      await getFeatures()
    }

    return () => fetchFeatures()
  }, [fetchInitialState, getFeatures, institutionalAccountUnderReview])

  if (status === 'loading') {
    return <Loading />
  }

  if (status === 'error') {
    return <ErrorMessage />
  }

  const { investments, totalBalance } = props.account
  const { plaidLinkedAccountIds, features, isInvestmentsReview } = props.store
  const { rothContribAllowed, posttaxContribAllowed } = props.account.account
  const canEdit = type !== 'institutional' || features?.editInstitutionalAccount
  // Plaid does not split balances by tax treatment, so eligible accounts need this warning.
  const showSplitBalanceWarning =
    _.includes(plaidLinkedAccountIds, Number(id)) && (rothContribAllowed || posttaxContribAllowed)
  // Set when the user arrives here from a processed statement, so the copy asks them to check
  // what the AI extracted rather than to add more.
  const reviewing = isInvestmentsReview(id)
  const accountPath = `/accounts/${type}/${accountType}/${id}`
  const goToAccount = () => props.navigate(accountPath)

  /*
   * Mobile opens straight onto the security search with the keyboard up, which has to be raised from
   * inside this tap — the field it belongs to does not exist until the next screen mounts. See
   * primeKeyboard.
   */
  const handleAddInvestment = () => {
    if (isMobile) primeKeyboard()
    props.navigate(`/accounts/add/${type}/${accountType}/${id}/investment`)
  }

  const actions = canEdit && (
    <InvestmentActions>
      {/* Uploading another statement mid-review would replace what the user is checking. */}
      {!reviewing && <UploadStatementButton onClick={openUpload} />}
      <AddInvestmentPill type='button' onClick={handleAddInvestment}>
        + Add investment
      </AddInvestmentPill>
    </InvestmentActions>
  )

  return (
    <div
      css={css`
        max-width: 1200px;
        margin: auto;
        padding: 1rem;
      `}>
      <Container>
        <PageHeader>
          <HeaderMain>
            <PageHeading>{reviewing ? 'Review investments' : 'Add investments'}</PageHeading>
            {reviewing && (
              <ReviewNote>
                {UPLOAD_COPY.reviewNote}{' '}
                <Link to='/ai-disclaimer'>{UPLOAD_COPY.disclaimerLink}</Link>
              </ReviewNote>
            )}
          </HeaderMain>
          <BalanceSummary>
            <BalanceLabel>Account balance</BalanceLabel>
            <BalanceValue>
              <BalanceSymbol>$</BalanceSymbol>
              <BalanceDigits>{numberToDollars(totalBalance, true)}</BalanceDigits>
            </BalanceValue>
            {actions}
          </BalanceSummary>
        </PageHeader>

        {isInstitutionalAccount ? (
          <InstitutionalInvestmentsDisplay />
        ) : investments.length ? (
          <InvestmentsDisplay showSplitBalanceWarning={showSplitBalanceWarning} />
        ) : (
          /* Mobile's design leaves the empty state to the buttons alone. */
          !isMobile && <EmptyStateBox>You haven’t added any investments yet.</EmptyStateBox>
        )}

        {investments.length ? (
          <DoneRow>
            <Button variant='primary' type='button' onClick={goToAccount}>
              All investments added
            </Button>
          </DoneRow>
        ) : isMobile ? (
          /* Mobile draws skipping as a link, and spells it out. */
          <SkipTextLink type='button' onClick={goToAccount}>
            Skip investments for now
          </SkipTextLink>
        ) : (
          <SkipButton type='button' onClick={goToAccount}>
            Skip for now
          </SkipButton>
        )}
      </Container>
    </div>
  )
}

export default withRouterProps(inject('account', 'store')(observer(AddInvestments)))
